"use client";

import { useState, useEffect, useCallback } from "react";
import { useUIStore } from "@/stores/ui-store";
import { useGameStore } from "@/stores/game-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, CheckCircle, Circle, Clock, AlertCircle, Trash2 } from "lucide-react";

interface Task {
  id: string;
  projectId: string;
  surggieId: string | null;
  sectionId: string | null;
  title: string;
  description: string | null;
  status: "pending" | "in_progress" | "completed" | "failed";
  createdAt: Date;
}

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "#c5a83a", icon: Circle },
  in_progress: { label: "In Progress", color: "#3d6890", icon: Clock },
  completed: { label: "Done", color: "#5c7935", icon: CheckCircle },
  failed: { label: "Failed", color: "#c54a4a", icon: AlertCircle },
};


export function TasksPanel() {
  const setActivePanel = useUIStore((s) => s.setActivePanel);
  const surggies = useGameStore((s) => s.surggies);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "in_progress" | "completed">("all");
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newSurggieId, setNewSurggieId] = useState<string>("");
  const [creating, setCreating] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/tasks", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } catch (e) {
      console.error("Failed to fetch tasks:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreateTask = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await fetch("/api/tasks", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: newTitle,
          description: newDescription || null,
          surggieId: newSurggieId || null,
          projectId: "default",
        }),
      });
      if (res.ok) {
        setNewTitle("");
        setNewDescription("");
        setNewSurggieId("");
        setShowNewForm(false);
        fetchTasks();
      }
    } catch (e) {
      console.error("Failed to create task:", e);
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: Task["status"]) => {
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
        );
      }
    } catch (e) {
      console.error("Failed to update task:", e);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
      }
    } catch (e) {
      console.error("Failed to delete task:", e);
    }
  };

  const filteredTasks = tasks.filter((t) => {
    if (filter === "all") return true;
    return t.status === filter;
  });

  const pendingCount = tasks.filter((t) => t.status === "pending").length;

  return (
    <div className="absolute top-0 right-0 bottom-0 w-80 z-50 bg-[#f5ecd6] border-l-2 border-[#1a1a1a] flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b-2 border-[#1a1a1a]">
        <div className="flex items-center gap-2">
          <span className="text-[#1a1a1a] font-mono text-sm font-bold">Tasks</span>
          {pendingCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-[#c5a83a]/20 text-[#c5a83a] text-xs font-mono">
              {pendingCount}
            </span>
          )}
        </div>
        <button
          onClick={() => setActivePanel(null)}
          className="p-1 rounded hover:bg-[#b89968]/40 text-[#5c4a30] hover:text-[#1a1a1a] transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 p-2 border-b-2 border-[#1a1a1a]">
        {(["all", "pending", "in_progress", "completed"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2 py-1 rounded text-xs font-mono transition-colors cursor-pointer ${
              filter === f
                ? "bg-[#c5a83a] border border-[#1a1a1a] text-[#1a1a1a]"
                : "text-[#8a7a5c] hover:text-[#1a1a1a]/70"
            }`}
          >
            {f === "all" ? "All" : f === "in_progress" ? "Active" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* New task button */}
      <div className="p-2 border-b-2 border-[#1a1a1a]">
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded bg-[#3d6890]/10 border border-[#3d6890]/30 hover:bg-[#3d6890]/20 text-[#3d6890] text-sm font-mono transition-colors cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          New Task
        </button>
      </div>

      {/* New task form */}
      {showNewForm && (
        <div className="p-3 border-b-2 border-[#1a1a1a] space-y-3">
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Task title..."
            className="text-sm"
          />
          <Input
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional)..."
            className="text-sm"
          />
          <select
            value={newSurggieId}
            onChange={(e) => setNewSurggieId(e.target.value)}
            className="w-full px-2 py-1.5 rounded bg-[#fff7e6] border border-[#1a1a1a] text-[#1a1a1a] text-sm font-mono"
          >
            <option value="">Assign to Surggie (optional)</option>
            {surggies.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.role})
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNewForm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateTask}
              disabled={!newTitle.trim() || creating}
              className="flex-1"
            >
              {creating ? "..." : "Create"}
            </Button>
          </div>
        </div>
      )}

      {/* Task list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {loading && (
          <p className="text-[#8a7a5c] font-mono text-xs text-center mt-8">
            Loading tasks...
          </p>
        )}
        {!loading && filteredTasks.length === 0 && (
          <p className="text-[#8a7a5c] font-mono text-xs text-center mt-8">
            {filter === "all" ? "No tasks yet. Create one to get started!" : `No ${filter} tasks.`}
          </p>
        )}
        {filteredTasks.map((task) => {
          const config = STATUS_CONFIG[task.status];
          const Icon = config.icon;
          const assignedSurggie = task.surggieId
            ? surggies.find((s) => s.id === task.surggieId)
            : null;

          return (
            <div
              key={task.id}
              className="p-3 rounded-lg bg-[#fff7e6] border border-[#1a1a1a] hover:border-[#5c4a30] transition-colors"
            >
              <div className="flex items-start gap-2">
                <button
                  onClick={() => {
                    const nextStatus: Task["status"] =
                      task.status === "pending"
                        ? "in_progress"
                        : task.status === "in_progress"
                        ? "completed"
                        : "pending";
                    handleStatusChange(task.id, nextStatus);
                  }}
                  className="mt-0.5 cursor-pointer"
                >
                  <Icon
                    className="w-4 h-4"
                    style={{ color: config.color }}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-mono ${task.status === "completed" ? "line-through text-[#8a7a5c]" : "text-[#1a1a1a]"}`}>
                    {task.title}
                  </p>
                  {task.description && (
                    <p className="text-xs text-[#8a7a5c] font-mono mt-1 truncate">
                      {task.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-mono"
                      style={{ backgroundColor: `${config.color}20`, color: config.color }}
                    >
                      {config.label}
                    </span>
                    {assignedSurggie && (
                      <span className="text-[10px] text-[#8a7a5c] font-mono">
                        → {assignedSurggie.name}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteTask(task.id)}
                  className="p-1 rounded hover:bg-[#b89968]/40 text-[#8a7a5c] hover:text-[#c54a4a] transition-colors cursor-pointer"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}